import type {
  DeviceHistoryEntry,
  DeviceStatus,
  LifecycleAction,
  ShipmentKind,
} from '@/features/device-management/types/deviceManagement';
import { msg } from '@lingui/core/macro';

export const DEVICE_MANAGEMENT_QUERY_KEY = ['device-management'] as const;

export const DEVICE_MANAGEMENT_PAGE_SIZE = 100;

export const DEVICE_EDITOR_ERROR_MESSAGES: Record<string, ReturnType<typeof msg>> = {
  device_identifier_conflict: msg`This device identifier is already in use.`,
  effective_date_conflict: msg`This effective date conflicts with a later device record. Choose a later date.`,
  invalid_device_value: msg`The selected device type is no longer available. Choose another type.`,
};

export const DEVICE_EDITOR_ERROR_FALLBACK = msg`Could not save the device. Please try again.`;

export const DEVICE_STATUS_VALUES = [
  'onboarding',
  'active',
  'inactive',
  'under-calibration',
  'sent-for-repair',
  'broken',
  'replaced',
] as const satisfies readonly DeviceStatus[];

export const DEVICE_STATUS_LABELS: Record<DeviceStatus, ReturnType<typeof msg>> = {
  onboarding: msg`Onboarding`,
  active: msg`Active`,
  inactive: msg`Inactive`,
  'under-calibration': msg`Under calibration`,
  'sent-for-repair': msg`Sent for repair`,
  broken: msg`Broken`,
  replaced: msg`Replaced`,
};

export const DEVICE_STATUS_DESCRIPTIONS: Record<DeviceStatus, ReturnType<typeof msg>> = {
  onboarding: msg`Registered and not yet used in a field log.`,
  active: msg`Used in a field log yesterday.`,
  inactive: msg`No field log or footage yesterday.`,
  'under-calibration': msg`Being calibrated. The owner does not change.`,
  'sent-for-repair': msg`Out for repair with the same owner. The device ID may change when it returns.`,
  broken: msg`Broken and still with the vendor.`,
  replaced: msg`Taken out of service and replaced by another device.`,
};

export const DEVICE_TRANSIT_BADGE_VARIANTS = {
  'in-transit': 'outline',
  overdue: 'warning',
} as const;

export const DEVICE_TRANSIT_BADGE_LABELS = {
  'in-transit': msg`In transit`,
  overdue: msg`Overdue`,
} as const;

export const DEVICE_TRANSIT_BADGE_LABEL_FALLBACK = msg`Transit status`;

export const SHIPMENT_KIND_VALUES = [
  'under-calibration',
  'sent-for-repair',
  'returned',
  'moved',
] as const satisfies readonly ShipmentKind[];

export const SHIPMENT_KIND_LABELS: Record<ShipmentKind, ReturnType<typeof msg>> = {
  'under-calibration': msg`Calibration`,
  'sent-for-repair': msg`Repair`,
  returned: msg`Return`,
  moved: msg`Transfer`,
};

export const LIFECYCLE_ACTION_LABELS: Record<LifecycleAction, ReturnType<typeof msg>> = {
  'set-status': msg`Set status`,
  'under-calibration': msg`Send for calibration`,
  'sent-for-repair': msg`Send for repair`,
  broken: msg`Mark as broken`,
  returned: msg`Return device`,
  moved: msg`Transfer device`,
  received: msg`Confirm receipt`,
  replaced: msg`Replace device`,
};

export const LIFECYCLE_ACTION_DIALOG_TITLES: Record<LifecycleAction, ReturnType<typeof msg>> = {
  'set-status': msg`Change device status`,
  'under-calibration': msg`Send device for calibration`,
  'sent-for-repair': msg`Send device for repair`,
  broken: msg`Mark device as broken`,
  returned: msg`Return device`,
  moved: msg`Transfer device`,
  received: msg`Confirm device receipt`,
  replaced: msg`Replace device`,
};

export const LIFECYCLE_ACTION_SUBMIT_LABELS: Record<LifecycleAction, ReturnType<typeof msg>> = {
  'set-status': msg`Save status`,
  'under-calibration': msg`Send for calibration`,
  'sent-for-repair': msg`Send for repair`,
  broken: msg`Mark as broken`,
  returned: msg`Return device`,
  moved: msg`Transfer device`,
  received: msg`Mark received`,
  replaced: msg`Replace device`,
};

export const DEVICE_LIFECYCLE_ERROR_MESSAGES: Record<string, ReturnType<typeof msg>> = {
  action_not_allowed: msg`This operation is not available for this device or history record.`,
  commit_outcome_unknown: msg`We could not confirm this update. Retry the original request.`,
  database_unavailable: msg`The device service is temporarily unavailable. Try again later.`,
  device_identifier_conflict: msg`This device identifier is already in use.`,
  device_in_transit: msg`Mark the current shipment as received before changing this device.`,
  effective_date_conflict: msg`This date conflicts with the device history. Choose another date.`,
  forbidden: msg`You do not have permission to update this device.`,
  history_incomplete: msg`The device history is incomplete, so this change cannot be recorded safely.`,
  idempotency_conflict: msg`This request no longer matches the original update. Start again.`,
  invalid_device_value: msg`Check the operation details and try again.`,
  invalid_request: msg`Check the operation details and try again.`,
  not_found: msg`This device is no longer available. Refresh and try again.`,
  replacement_requires_organization: msg`Assign this device to an organization before replacing it.`,
  replacement_unavailable: msg`This device can no longer be replaced. Refresh and try again.`,
  status_unchanged: msg`This device already has that status.`,
  version_conflict: msg`This device changed elsewhere. Review the refreshed details and try again.`,
};

export const DEVICE_HISTORY_RELATED_DEVICE_LABELS: Record<string, ReturnType<typeof msg>> = {
  'replaced-by': msg`Replaced by:`,
  replaces: msg`Replaces:`,
};

export const DEVICE_HISTORY_RELATED_DEVICE_FALLBACK = msg`Related device:`;

export const DEVICE_LIFECYCLE_ERROR_FALLBACK = msg`Could not record this device operation. Refresh the device and try again.`;

export const DEVICE_HISTORY_EVENT_LABELS: Record<
  DeviceHistoryEntry['eventType'],
  ReturnType<typeof msg>
> = {
  registered: msg`Registered`,
  'status-changed': msg`Status changed`,
  dispatched: msg`Dispatched`,
  received: msg`Received`,
  'organization-changed': msg`Organization changed`,
  returned: msg`Returned`,
  replaced: msg`Replaced`,
  'calibration-backfilled': msg`Calibration backfilled`,
  'dates-corrected': msg`Dates corrected`,
};

export const DEVICE_HISTORY_EVENT_VALUES = [
  'registered',
  'status-changed',
  'dispatched',
  'received',
  'organization-changed',
  'returned',
  'replaced',
  'calibration-backfilled',
  'dates-corrected',
] as const satisfies readonly DeviceHistoryEntry['eventType'][];

export const DEVICE_HISTORY_EVENT_FALLBACK = msg`Device updated`;

export const DEVICE_HISTORY_DATE_CHANGE_LABELS = {
  sentOn: msg`Sent date`,
  expectedOn: msg`Expected arrival`,
  receivedOn: msg`Received date`,
  occurredOn: msg`Event date`,
  firstActivatedOn: msg`First activation date`,
} as const;

export const DEVICE_AWAY_NEXT_STEPS: Partial<Record<DeviceStatus, ReturnType<typeof msg>>> = {
  'under-calibration': msg`At the calibration lab. When calibration is done, send it back to the owner with the shipment dates.`,
  'sent-for-repair': msg`At the repair shop. When the repair is done, send it back to the owner with the shipment dates.`,
};

export const SHIPMENT_DATES_DIALOG_TITLES = {
  current: msg`Correct shipment dates`,
  'calibration-outbound': msg`Correct outbound calibration dates`,
  'calibration-return': msg`Correct return calibration dates`,
} as const;
